/**
 * Task Detail Page - FLO SIGNATURE DESIGN SYSTEM v2
 * Clean. Controlled. Consistent. Light ops mode.
 */

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowPathIcon } from '@heroicons/react/24/outline';
import TaskForm from '../components/forms/TaskForm';
import { apiClient } from '../api/apiConfig';
import { Task } from '../types/task';
import { COLORS, SPACING, TYPOGRAPHY } from '../design/tokens';
import { Panel } from '../design/primitives';

const TaskDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [task, setTask] = useState<Task | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchTask = async () => {
      if (!id) return;
      setLoading(true);
      setError(null);
      try {
        const response = await apiClient.get(`/api/tasks/${id}/`);
        setTask(response.data);
      } catch (err) {
        console.error('Error fetching task:', err);
        setError('Failed to load task');
      } finally {
        setLoading(false);
      }
    };

    fetchTask();
  }, [id]);

  const handleClose = () => {
    navigate('/tasks');
  };

  const handleTaskUpdated = () => {
    if (!id) return;
    apiClient.get(`/api/tasks/${id}/`).then(response => {
      setTask(response.data);
    });
  };

  if (loading) {
    return (
      <div style={{
        backgroundColor: COLORS.bg.base,
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: SPACING[24]
      }}>
        <ArrowPathIcon style={{
          width: '32px',
          height: '32px',
          color: COLORS.accent.default,
          animation: 'spin 1s linear infinite'
        }} />
      </div>
    );
  }

  if (error || !task) {
    return (
      <div style={{
        backgroundColor: COLORS.bg.base,
        minHeight: '100vh',
        padding: SPACING[24]
      }}>
        <div style={{ maxWidth: '600px', margin: '0 auto' }}>
          <Panel>
            <p style={{
              fontFamily: TYPOGRAPHY.body.fontFamily,
              fontSize: TYPOGRAPHY.body.sizes.default,
              color: COLORS.status.error,
              margin: 0
            }}>
              {error || 'Task not found'}
            </p>
          </Panel>
        </div>
      </div>
    );
  }

  return (
    <TaskForm
      isOpen={true}
      onClose={handleClose}
      onTaskCreated={() => {}}
      onTaskUpdated={handleTaskUpdated}
      initialTaskData={task}
      isEditMode={true}
    />
  );
};

export default TaskDetailPage;
